import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Clock, Target, Users, Star, Tag } from "lucide-react";
import { getArticlesByCategoryName } from "@/data/articles";

import childrenImage from "@/assets/children-upload-optimized.jpg";
import teensImage from "@/assets/teens-upload-optimized.jpg";
import adultsImage from "@/assets/adults-upload-optimized.jpg";
import agonistiImage from "@/assets/agonisti-upload-optimized.jpg";

const percorsi = [
  {
    id: "bambini",
    title: "Bambini 6-10 anni",
    subtitle: "Le basi della crescita",
    image: childrenImage,
    description: "Un percorso di apprendimento ludico-motorio pensato per i più piccoli, dove il gioco diventa strumento educativo. Attraverso attività mirate, i bambini consolidano gli schemi motori di base e sviluppano le capacità coordinative, cognitive e condizionali.",
    obiettivi: [
      "Apprendimento ludico-motorio",
      "Consolidamento degli schemi motori di base",
      "Sviluppo delle capacità coordinative, cognitive e condizionali",
      "Disciplina e rispetto delle regole",
      "Socializzazione",
      "Costruzione dell'autostima"
    ],
    allenamento: [
      "Giochi ed esercizi di coordinazione",
      "Tecniche di base semplificate",
      "Forme (poomsae) introduttive",
      "Attività di gruppo e giochi a squadre"
    ],
    frequenza: "2-3 volte a settimana",
    durata: "60-90 minuti per lezione",
    adatto: "Bambini curiosi, energici, anche senza esperienza sportiva"
  },
  {
    id: "ragazzi",
    title: "Ragazzi 11-15 anni",
    subtitle: "Costruire le fondamenta",
    image: teensImage,
    description: "L'adolescenza è un momento di trasformazione. Il Taekwondo aiuta i ragazzi a canalizzare le energie, sviluppare fiducia in sé stessi e affrontare le sfide con determinazione, aprendo anche la strada all'esperienza pre-agonistica.",
    obiettivi: [
      "Canalizzazione dell'energia",
      "Sviluppo della fiducia in sé e dell'autostima",
      "Problem solving e autoefficacia",
      "Avviamento a un'impronta agonistica",
      "Potenziamento fisico e preparazione atletica",
      "Lavoro tecnico-tattico e strategia di gara"
    ],
    allenamento: [
      "Tecniche di calcio e pugno avanzate",
      "Forme (poomsae) di livello intermedio",
      "Combattimento controllato con protezioni",
      "Preparazione atletica specifica"
    ],
    frequenza: "3 volte a settimana",
    durata: "60-90 minuti per lezione",
    adatto: "Ragazzi che cercano uno sport completo e formativo"
  },
  {
    id: "adulti",
    title: "Adulti",
    subtitle: "Mai troppo tardi per iniziare",
    image: adultsImage,
    description: "Che tu sia un principiante assoluto o un praticante che vuole riprendere, il nostro corso adulti offre un percorso su misura orientato al miglioramento della forma fisica, all'apprendimento tecnico, alla riduzione dello stress e alla crescita personale.",
    obiettivi: [
      "Miglioramento della forma fisica generale",
      "Apprendimento tecnico progressivo",
      "Riduzione dello stress",
      "Crescita personale attraverso obiettivi concreti"
    ],
    allenamento: [
      "Riscaldamento mirato",
      "Stretching",
      "Tecniche di base e forme (poomsae)",
      "Sparring leggero"
    ],
    frequenza: "2-3 volte a settimana",
    durata: "90 minuti per lezione",
    adatto: "Adulti di ogni età, anche senza esperienza nelle arti marziali"
  },
  {
    id: "agonisti",
    title: "Giovani Agonisti",
    subtitle: "Per chi vuole competere",
    image: agonistiImage,
    description: "Un percorso dedicato a chi desidera mettersi alla prova nelle competizioni. Allenamenti intensivi, preparazione atletica specifica e supporto costante per raggiungere i propri obiettivi sportivi.",
    obiettivi: [
      "Preparazione tecnico-tattica avanzata",
      "Condizione fisica ottimale",
      "Gestione della competizione",
      "Crescita nel ranking federale"
    ],
    allenamento: [
      "Video analisi tecnica",
      "Partecipazione a gare federali",
      "Stage specialistici",
      "Allenamenti mirati alla performance"
    ],
    frequenza: "4-5 volte a settimana",
    durata: "90-120 minuti per lezione",
    adatto: "Atleti motivati con esperienza nel Taekwondo"
  }
];

const categoryLabels: Record<string, string> = {
  bambini: "Bambini", ragazzi: "Ragazzi", adulti: "Adulti",
  generale: "Generale", zona: "Zona", agonisti: "Agonisti"
};

const Percorsi = () => {
  const blogArticles = [
    ...getArticlesByCategoryName("bambini", 1),
    ...getArticlesByCategoryName("ragazzi", 1),
    ...getArticlesByCategoryName("adulti", 1),
  ].slice(0, 3);
  return (
    <Layout>
      <Helmet>
        <title>I Percorsi - Taekwondo Mattei Roma</title>
        <meta name="description" content="Percorsi di Taekwondo per bambini 6-10 anni, ragazzi 11-15 anni, adulti e giovani agonisti. Un programma su misura per ogni età." />
        <link rel="canonical" href="https://taekwondomattei.com/percorsi" />
        <meta property="og:title" content="I Percorsi - Taekwondo Mattei Roma" />
        <meta property="og:description" content="Percorsi di Taekwondo per bambini 6-10 anni, ragazzi 11-15 anni, adulti e giovani agonisti. Un programma su misura per ogni età." />
        <meta property="og:image" content="https://taekwondomattei.com/og-percorsi.jpg" />
        <meta property="og:url" content="https://taekwondomattei.com/percorsi" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="I Percorsi - Taekwondo Mattei Roma" />
        <meta name="twitter:description" content="Percorsi di Taekwondo per bambini, ragazzi, adulti e agonisti. Un programma su misura per ogni età." />
        <meta name="twitter:image" content="https://taekwondomattei.com/og-percorsi.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Percorsi di Taekwondo",
          "itemListElement": percorsi.map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Course",
              "name": p.title,
              "description": p.description,
              "provider": { "@type": "Organization", "name": "Taekwondo Mattei" }
            }
          }))
        })}</script>
      </Helmet>
      <SeoBreadcrumb items={[{ label: "I Percorsi" }]} />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              I Percorsi
            </h1>
            <p className="font-body text-xl text-muted-foreground">
              Un programma per ogni età e obiettivo. Dalla scoperta del movimento 
              alla preparazione agonistica, ogni percorso è pensato per accompagnarti 
              nella crescita.
            </p>
          </div>
        </div>
      </section>

      {/* Percorsi */}
      {percorsi.map((percorso, index) => (
        <section
          key={percorso.id}
          id={percorso.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-background' : 'bg-secondary'}`}
        >
          <div className="container-wide mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
               <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                 <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-large w-full">
                    <img
                      src={percorso.image}
                      alt={percorso.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 hero-overlay-soft pointer-events-none" />
                 </div>
               </div>
              
              <div className={`space-y-8 ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                <div>
                  <span className="font-body text-sm font-medium text-gold uppercase tracking-wider">
                    {percorso.subtitle}
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                    {percorso.title}
                  </h2>
                  <p className="font-body text-lg text-muted-foreground leading-relaxed">
                    {percorso.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-gold" />
                      <h3 className="font-display text-lg font-semibold text-foreground">Obiettivi</h3>
                    </div>
                    <ul className="space-y-2">
                      {percorso.obiettivi.map((obj) => (
                        <li key={obj} className="font-body text-sm text-muted-foreground flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                          {obj}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-gold" />
                      <h3 className="font-display text-lg font-semibold text-foreground">Cosa si allena</h3>
                    </div>
                    <ul className="space-y-2">
                      {percorso.allenamento.map((item) => (
                        <li key={item} className="font-body text-sm text-muted-foreground flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-wrap gap-6 pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <span className="font-body text-sm text-muted-foreground">
                      {percorso.frequenza} • {percorso.durata}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <span className="font-body text-sm text-muted-foreground">
                      {percorso.adatto}
                    </span>
                  </div>
                </div>

                <Link to="/lezione-di-prova" className="btn-accent inline-flex">
                  Prenota una prova
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Articoli correlati */}
      {blogArticles.length > 0 && (
        <section className="section-padding bg-background">
          <div className="container-wide mx-auto">
            <SectionHeader
              title="Approfondisci dal blog"
              subtitle="Articoli sui nostri percorsi formativi"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="group card-elevated border border-border p-6"
                >
                  <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                    {categoryLabels[article.category] || article.category}
                  </span>
                  <h3 className="font-display text-lg font-semibold text-foreground mt-4 group-hover:text-gold transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
                    {article.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-link */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <h2 className="sr-only">Scopri anche</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/maestri" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                I Maestri →
              </h3>
              <p className="font-body text-muted-foreground">
                Conosci i nostri istruttori federali qualificati.
              </p>
            </Link>
            <Link to="/academy" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                L'Academy →
              </h3>
              <p className="font-body text-muted-foreground">
                La nostra storia, il metodo e l'approccio educativo.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold mb-6">
            Non sai quale percorso scegliere?
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            Vieni a trovarci per una lezione di prova gratuita. Ti aiuteremo a trovare 
            il percorso più adatto a te o ai tuoi figli.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Percorsi;
